import { DataGridPro as DataGrid } from '@mui/x-data-grid-pro';
import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useStyles } from './styles/DataTable.styles';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { createColumn } from './CellContent';

import type { DataTableProps, WithID } from './types';

/**
 * Create a responsive table with pagination, column re-ordering, filtering, etc.
 *
 * Uses MUI {@link DataGrid} under the hood, and will forward overriding props to
 * the rendered MUI component.
 */
export const DataTable = <Row extends Record<string, any>>({
    storageKey,
    getUniqueRowId,
    renderExpandedContent,
    onPaginate,
    rows,
    columns,
    pageSize,
    totalRowCount,
    virtualize = true,
    menus = false,
    columnOrdering = false,
    height = 600,
    displayTotal = true,
    showFirstButton = true,
    showLastButton = true,
    showBackButton = true,
    page,
    ...props
}: DataTableProps<Row>) => {
    const classes = useStyles();

    const pageSizeOptions = useMemo(() => [pageSize], [pageSize]);
    const [columnOrder, setColumnOrder] = useLocalStorage<string[]>({
        key: `DataTable-${storageKey}-Column-Order`,
        value: columns.map(({ field }) => field),
    });

    const columnData = useMemo(() => columns.map(createColumn), [columns]);

    return (
        <Box
            display='flex'
            flexDirection='column'
            height={height}
            sx={{
                width: '100%',
                minWidth: 0,
                flex: 1,
            }}>
            {displayTotal && (
                <Typography alignSelf='end'>
                    Total:{' '}
                    <b>
                        {new Intl.NumberFormat(undefined, {
                            compactDisplay: 'short',
                        }).format(totalRowCount)}
                    </b>
                </Typography>
            )}
            <DataGrid
                disableVirtualization={!virtualize}
                onPaginationModelChange={({ page }) => onPaginate?.(page + 1)}
                // Data
                rows={rows.map((row) => ({ ...row, id: getUniqueRowId(row as Row) } as WithID<Row>))}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                columns={columnData}
                // Detail panel
                {...(typeof renderExpandedContent === 'function'
                    ? {
                          getDetailPanelHeight: () => 'auto',
                          getDetailPanelContent: ({ row }) => renderExpandedContent(row as WithID<Row>),
                      }
                    : undefined)}
                getDetailPanelHeight={() => 'auto'}
                // Pagination & filtering
                pagination
                paginationMode='server'
                rowsLoadingMode='server'
                filterMode='server'
                sortingMode='server'
                pageSizeOptions={pageSizeOptions}
                rowCount={totalRowCount}
                // Styling
                columnHeaderHeight={45}
                rowHeight={50}
                className={classes.table}
                // Disabling certain DataGrid features
                disableDensitySelector
                disableColumnResize
                disableRowSelectionOnClick
                disableColumnMenu={!menus}
                paginationModel={{
                    pageSize,
                    page: page - 1,
                }}
                // Preparing state
                initialState={{
                    pagination: {
                        paginationModel: {
                            // ? Must default to prevent bugs around pagination
                            pageSize,
                            page: 0,
                        },
                    },
                    sorting: {
                        sortModel: columnData.reduce((acc, { field, sortable }) => {
                            if (!sortable) return acc;

                            acc.push({ field, sort: 'desc' });

                            return acc;
                        }, [] as { field: string; sort: 'asc' | 'desc' }[]),
                    },
                    // If column reordering isn't disabled
                    ...(columnOrdering
                        ? {
                              columns: {
                                  orderedFields: columnOrder,
                              },
                          }
                        : {}),
                }}
                onColumnOrderChange={(_, __, data) => {
                    const { api } = data as { api: { state: { columns: { orderedFields: string[] } } } };
                    setColumnOrder(api.state.columns.orderedFields);
                }}
                // Enabling features
                slotProps={{
                    pagination: {
                        showFirstButton,
                        showLastButton,
                        ...(!showBackButton
                            ? {
                                  backIconButtonProps: {
                                      sx: {
                                          display: 'none',
                                      },
                                  },
                              }
                            : {}),
                        ...(!displayTotal
                            ? {
                                  sx: {
                                      '& .MuiTablePagination-displayedRows': {
                                          display: 'none',
                                      },
                                  },
                              }
                            : {}),
                    },
                }}
                {...props}
            />
        </Box>
    );
};
